// Web port of teamplayer/app/view-routes/_hooks/useRoutePlayback.ts
// Playback engine: requestAnimationFrame tick loop with a virtual clock, speed
// multiplier, idle "halt" pauses, seek, and linear interpolation of position.
// Logic is intentionally identical to the mobile hook; only types are stripped
// and positions use { lat, lng } keys.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";

const TARGET_REPLAY_MS = {
  slow: 180_000,
  normal: 60_000,
  fast: 20_000,
};

const HALT_PAUSE_MS = 1000;

const lerp = (a, b, t) => a + (b - a) * t;

const findIndexBefore = (points, virtualTime) => {
  if (points.length === 0) return 0;
  let lo = 0;
  let hi = points.length - 1;
  while (lo < hi) {
    const mid = (lo + hi + 1) >>> 1;
    if (points[mid].timestamp <= virtualTime) {
      lo = mid;
    } else {
      hi = mid - 1;
    }
  }
  return lo;
};

export const useRoutePlayback = ({ routePoints, idlePoints }) => {
  const startTs = routePoints.length > 0 ? routePoints[0].timestamp : 0;
  const endTs =
    routePoints.length > 0 ? routePoints[routePoints.length - 1].timestamp : 0;
  const totalSpan = Math.max(endTs - startTs, 0);

  const [isPlaying, setIsPlaying] = useState(false);
  const [speed, setSpeed] = useState("normal");
  const [virtualTime, setVirtualTime] = useState(startTs);
  const [activeHaltIndex, setActiveHaltIndex] = useState(null);

  const rafRef = useRef(null);
  const lastFrameRef = useRef(0);
  const haltPauseUntilRef = useRef(0);
  const consumedHaltsRef = useRef(new Set());

  // Refs mirror state so the RAF tick can read fresh values without being
  // re-created (which would tear down the loop every frame).
  const virtualTimeRef = useRef(startTs);
  const activeHaltIndexRef = useRef(null);
  const speedRef = useRef("normal");
  const haltWindowsRef = useRef([]);
  const endTsRef = useRef(endTs);
  const totalSpanRef = useRef(totalSpan);

  const haltWindows = useMemo(() => {
    return (idlePoints || [])
      .map((idle, index) => ({
        index,
        start: new Date(idle.startTime).getTime(),
        end: new Date(idle.endTime).getTime(),
      }))
      .filter((w) => Number.isFinite(w.start) && Number.isFinite(w.end))
      .sort((a, b) => a.start - b.start);
  }, [idlePoints]);

  useEffect(() => {
    haltWindowsRef.current = haltWindows;
  }, [haltWindows]);

  useEffect(() => {
    speedRef.current = speed;
  }, [speed]);

  useEffect(() => {
    endTsRef.current = endTs;
    totalSpanRef.current = totalSpan;
  }, [endTs, totalSpan]);

  const updateVirtualTime = useCallback((vt) => {
    virtualTimeRef.current = vt;
    setVirtualTime(vt);
  }, []);

  const updateActiveHaltIndex = useCallback((idx) => {
    activeHaltIndexRef.current = idx;
    setActiveHaltIndex(idx);
  }, []);

  // Reset whenever the route changes
  useEffect(() => {
    setIsPlaying(false);
    updateVirtualTime(startTs);
    updateActiveHaltIndex(null);
    haltPauseUntilRef.current = 0;
    consumedHaltsRef.current = new Set();
  }, [startTs, endTs, updateVirtualTime, updateActiveHaltIndex]);

  const stopRaf = useCallback(() => {
    if (rafRef.current != null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
  }, []);

  const tick = useCallback(
    (now) => {
      if (lastFrameRef.current === 0) {
        lastFrameRef.current = now;
        rafRef.current = requestAnimationFrame(tick);
        return;
      }

      const wallDelta = now - lastFrameRef.current;
      lastFrameRef.current = now;

      if (haltPauseUntilRef.current > now) {
        rafRef.current = requestAnimationFrame(tick);
        return;
      }

      if (activeHaltIndexRef.current != null) {
        const halt = haltWindowsRef.current.find(
          (w) => w.index === activeHaltIndexRef.current
        );
        updateActiveHaltIndex(null);
        if (halt) {
          consumedHaltsRef.current.add(halt.index);
          const resumed = Math.max(virtualTimeRef.current, halt.end + 1);
          updateVirtualTime(resumed);
        }
        rafRef.current = requestAnimationFrame(tick);
        return;
      }

      const span = totalSpanRef.current;
      const multiplier =
        span > 0 ? span / TARGET_REPLAY_MS[speedRef.current] : 0;
      const prevVirtual = virtualTimeRef.current;
      const nextVirtual = prevVirtual + wallDelta * multiplier;

      const enteringHalt = haltWindowsRef.current.find(
        (w) =>
          !consumedHaltsRef.current.has(w.index) &&
          w.start >= prevVirtual &&
          w.start <= nextVirtual
      );

      if (enteringHalt) {
        updateVirtualTime(enteringHalt.start);
        updateActiveHaltIndex(enteringHalt.index);
        haltPauseUntilRef.current = now + HALT_PAUSE_MS;
        rafRef.current = requestAnimationFrame(tick);
        return;
      }

      if (nextVirtual >= endTsRef.current) {
        updateVirtualTime(endTsRef.current);
        setIsPlaying(false);
        return;
      }

      updateVirtualTime(nextVirtual);
      rafRef.current = requestAnimationFrame(tick);
    },
    [updateActiveHaltIndex, updateVirtualTime]
  );

  useEffect(() => {
    if (!isPlaying) {
      stopRaf();
      lastFrameRef.current = 0;
      return;
    }
    if (routePoints.length < 2) {
      setIsPlaying(false);
      return;
    }
    lastFrameRef.current = 0;
    rafRef.current = requestAnimationFrame(tick);
    return () => {
      stopRaf();
    };
  }, [isPlaying, routePoints.length, stopRaf, tick]);

  useEffect(() => stopRaf, [stopRaf]);

  const play = useCallback(() => {
    if (routePoints.length < 2) return;
    if (virtualTimeRef.current >= endTsRef.current) {
      updateVirtualTime(startTs);
      consumedHaltsRef.current = new Set();
    }
    setIsPlaying(true);
  }, [routePoints.length, startTs, updateVirtualTime]);

  const pause = useCallback(() => setIsPlaying(false), []);

  const toggle = useCallback(() => {
    setIsPlaying((p) => {
      if (!p) {
        if (routePoints.length < 2) return false;
        if (virtualTimeRef.current >= endTsRef.current) {
          updateVirtualTime(startTs);
          consumedHaltsRef.current = new Set();
        }
        return true;
      }
      return false;
    });
  }, [routePoints.length, startTs, updateVirtualTime]);

  const seekTo = useCallback(
    (progress) => {
      const span = totalSpanRef.current;
      if (span <= 0) return;
      const clamped = Math.min(1, Math.max(0, progress));
      const target = startTs + clamped * span;
      updateVirtualTime(target);
      updateActiveHaltIndex(null);
      haltPauseUntilRef.current = 0;
      const consumed = new Set();
      haltWindowsRef.current.forEach((w) => {
        if (w.end < target) consumed.add(w.index);
      });
      consumedHaltsRef.current = consumed;
    },
    [startTs, updateActiveHaltIndex, updateVirtualTime]
  );

  const reset = useCallback(() => {
    setIsPlaying(false);
    updateVirtualTime(startTs);
    updateActiveHaltIndex(null);
    haltPauseUntilRef.current = 0;
    consumedHaltsRef.current = new Set();
  }, [startTs, updateActiveHaltIndex, updateVirtualTime]);

  const { currentIndex, currentPosition } = useMemo(() => {
    if (routePoints.length === 0)
      return { currentIndex: 0, currentPosition: null };
    if (routePoints.length === 1) {
      const p = routePoints[0];
      return { currentIndex: 0, currentPosition: { lat: p.lat, lng: p.lng } };
    }
    const i = findIndexBefore(routePoints, virtualTime);
    if (i >= routePoints.length - 1) {
      const last = routePoints[routePoints.length - 1];
      return {
        currentIndex: routePoints.length - 1,
        currentPosition: { lat: last.lat, lng: last.lng },
      };
    }
    const a = routePoints[i];
    const b = routePoints[i + 1];
    const span = b.timestamp - a.timestamp;
    const frac =
      span > 0
        ? Math.min(1, Math.max(0, (virtualTime - a.timestamp) / span))
        : 0;
    return {
      currentIndex: i,
      currentPosition: {
        lat: lerp(a.lat, b.lat, frac),
        lng: lerp(a.lng, b.lng, frac),
      },
    };
  }, [routePoints, virtualTime]);

  const progress = totalSpan > 0 ? (virtualTime - startTs) / totalSpan : 0;
  const canPlay = routePoints.length >= 2;

  return {
    isPlaying,
    speed,
    setSpeed,
    progress,
    virtualTime,
    currentPosition,
    currentIndex,
    activeHaltIndex,
    canPlay,
    play,
    pause,
    toggle,
    seekTo,
    reset,
    startTs,
    endTs,
  };
};

export default useRoutePlayback;
